import { useRef, useState } from 'react';
import { ImagePlus, Trash2 } from 'lucide-react';
import { Button, cx } from './form.tsx';

/**
 * Image picker with preview. `onUpload` sends the file and returns the stored key + public url;
 * the parent keeps the key in its form state.
 */
export function ImageUpload({
  url,
  onUpload,
  onClear,
  aspect = 'aspect-video',
  hint,
  accept = 'image/png,image/jpeg,image/webp,image/svg+xml,image/x-icon',
  fit = 'contain',
  frameClassName = 'max-w-md',
}: {
  url: string | null;
  onUpload: (file: File) => Promise<void>;
  onClear?: () => void;
  aspect?: string;
  hint?: string;
  accept?: string;
  /** cover = show the picture cropped to the frame, centred (how the site will crop it). */
  fit?: 'contain' | 'cover';
  /** Width of the picture frame only; the buttons and hint below keep the full width (LPSS-34). */
  frameClassName?: string;
}) {
  const input = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  async function pick(file: File | undefined) {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      setError('檔案不可大於 5MB');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await onUpload(file);
    } catch (e) {
      setError(e instanceof Error ? e.message : '上載失敗');
    } finally {
      setBusy(false);
      if (input.current) input.current.value = '';
    }
  }
  return (
    <div className="space-y-2">
      <div className={cx('relative flex w-full items-center justify-center overflow-hidden rounded-xl border border-dashed border-line bg-canvas', frameClassName, aspect)}>
        {url ? (
          <img src={url} alt="" className={cx('h-full w-full', fit === 'cover' ? 'object-cover object-center' : 'object-contain')} />
        ) : (
          <button type="button" onClick={() => input.current?.click()} className="flex flex-col items-center gap-2 p-6 text-muted hover:text-ink">
            <ImagePlus className="size-7" />
            <span className="text-sm">選擇圖片</span>
          </button>
        )}
      </div>
      <div className="flex items-center gap-2">
        <Button size="sm" variant="secondary" loading={busy} onClick={() => input.current?.click()}>
          {url ? '更換圖片' : '上載圖片'}
        </Button>
        {url && onClear && (
          <Button size="sm" variant="ghost" icon={<Trash2 className="size-4" />} onClick={onClear}>
            移除
          </Button>
        )}
        {hint && <span className="text-xs text-muted">{hint}</span>}
      </div>
      {error && <p className="text-sm text-coral">{error}</p>}
      <input ref={input} type="file" accept={accept} hidden onChange={(e) => pick(e.target.files?.[0])} />
    </div>
  );
}
